// TODO: Resolve lint issues: SAAS-995

/* eslint-disable no-promise-executor-return */

/* Shared */
import { Defaults } from 'application/Shared/Constants';

/*  NPM  */
import {
    differenceInDays,
    isValid,
    parseISO,
    startOfDay,
    subDays
} from 'date-fns';
import {
    isEqual,
    isEqualWith,
    isEmpty as lodashIsEmpty,
    xorWith
} from 'lodash';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { validate as uuidValidate } from 'uuid';

import { ProviderType } from '../types/tenantProvider';

const Helpers = (() => {
    /**
     * Compares two table state values for deep equality
     *
     * @param a - First value to compare
     * @param b - Second value to compare
     * @returns boolean indicating if the values are equal
     *
     * Uses lodash's isEqualWith for deep comparison with custom handling for arrays.
     * For arrays, uses xorWith to find elements that differ between arrays and
     * considers them equal only if no differences are found.
     */
    const areEqual = (a, b) =>
        isEqualWith(a, b, (value1, value2) => {
            if (Array.isArray(value1) && Array.isArray(value2)) {
                const difference = xorWith(value1, value2, isEqual);
                return lodashIsEmpty(difference); // This is the same function as arraysEqual
            }
            return undefined;
        });

    /* https://stackoverflow.com/a/57166363 */
    /**
     * Compares two arrays for equality by checking length and elements
     * @param {Array} a - First array to compare
     * @param {Array} b - Second array to compare
     * @param {Array<string>} - Properties to ignore in comparison
     * @returns {boolean} True if arrays are equal, false otherwise
     */
    const arraysEqual = (a, b, ignoreProps = []) => {
        if (a.length !== b.length) return false;
        if (a === b) return true;

        /**
         * Creates a unique key for an object by converting it to a string
         * @param {*} obj - The object to create a key for
         * @returns {string} The unique key for the object
         */
        const createObjectKey = (obj) => {
            if (typeof obj !== 'object' || obj === null) return String(obj);

            if (Array.isArray(obj)) {
                return `[${obj
                    .map((item) => createObjectKey(item))
                    .sort()
                    .join(',')}]`;
            }

            // For objects, sort keys and recursively process values
            return `{${Object.keys(obj)
                .filter((key) => !ignoreProps.includes(key))
                .sort()
                .map((key) => `${key}:${createObjectKey(obj[key])}`)
                .join('|')}}`;
        };

        /** @type {Map<string, number>} */
        const mapA = new Map();
        /** @type {Map<string, number>} */
        const mapB = new Map();

        // Populate maps with object keys
        a.forEach((item) => {
            const key = createObjectKey(item);
            mapA.set(key, (mapA.get(key) || 0) + 1);
        });

        b.forEach((item) => {
            const key = createObjectKey(item);
            mapB.set(key, (mapB.get(key) || 0) + 1);
        });

        // Compare maps
        if (mapA.size !== mapB.size) return false;

        // eslint-disable-next-line no-restricted-syntax
        for (const [key, count] of mapA) {
            if (mapB.get(key) !== count) return false;
        }

        return true;
    };

    /**
     * Formats a byte value into a human readable string with appropriate size unit
     * @param {number} value - The size in bytes to format
     * @returns {string} Formatted string with size unit (e.g. "5 MB", "2.1 GB")
     * @example
     * byteSizeFormatter(1024) // Returns "1 KB"
     * byteSizeFormatter(1048576) // Returns "1 MB"
     * byteSizeFormatter(0) // Returns "0B"
     */
    const byteSizeFormatter = (value) => {
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        if (value === 0) return '0B';
        const i = parseInt(Math.floor(Math.log(value) / Math.log(1024)), 10);
        return `${Math.round(value / 1024 ** i)} ${sizes[i]}`;
    };

    /**
     * Clears all active toast notifications and optionally executes a callback function
     * @param {Function} [callback] - Optional callback function to execute after clearing toasts
     * @example
     * // Clear toasts without callback
     * clearToasts();
     *
     * // Clear toasts and execute callback
     * clearToasts(() => console.log('Toasts cleared'));
     */
    const clearToasts = (callback) => {
        toast.dismiss();
        if (typeof callback === 'function') {
            callback();
        }
    };

    /**
     * Converts a value to boolean by checking against common truthy values
     * @param {string|boolean|number|null|undefined} value - Value to convert to boolean
     * @returns {boolean} True if value matches 'true', true, '1' or 1, false otherwise
     * @example
     * convertToBoolean('true') // Returns true
     * convertToBoolean(1) // Returns true
     * convertToBoolean('1') // Returns true
     * convertToBoolean(true) // Returns true
     * convertToBoolean('false') // Returns false
     * convertToBoolean(0) // Returns false
     * convertToBoolean(null) // Returns false
     */
    const convertToBoolean = (value) => ['true', true, '1', 1].includes(value);

    /**
     * Counts the number of elements in an array or object
     * @param {Array|Object} obj - The array or object to count elements from
     * @returns {number} The count of elements (array length or number of object keys)
     * @example
     * count([1,2,3]) // Returns 3
     * count({a:1, b:2}) // Returns 2
     * count(null) // Returns 0
     * count("string") // Returns 0
     */
    const count = (obj) => {
        if (!obj) {
            return 0;
        }

        if (Array.isArray(obj)) {
            return obj.length;
        }

        if (typeof obj === 'object') {
            return Object.keys(obj).length;
        }

        return 0;
    };

    /**
     * Checks if a pie chart data object has any non-zero values
     * @param {Object} data - Object containing pie chart data where each key has a value property
     * @returns {boolean} True if any data point has a value greater than 0, false otherwise
     * @example
     * const data = {
     *   slice1: { value: 0 },
     *   slice2: { value: 5 }
     * };
     * doesPieChartHaveData(data); // Returns true
     */
    const doesPieChartHaveData = (data) => {
        const result = Object.keys(data).find((key) => data[key].value > 0);
        return !!result;
    };

    /**
     * Consolidates source information from multiple users into a single object
     * @param {Array<Object>} users - Array of user objects, each containing a sources property
     * @returns {Object} Object with source names as keys and binary values (1 if any user has that source, 0 if none do)
     * @example
     * const users = [
     *   { sources: { desktop: true, webLogin: false } },
     *   { sources: { desktop: false, webLogin: true } }
     * ];
     * getConsolidatesSourcesFromUsers(users);
     * // Returns { desktop: 1, webActivity: 0, webLogin: 1, download: 0, googleSSO: 0, microsoftSSO: 0, azure: 0, google: 0 }
     */
    const getConsolidatesSourcesFromUsers = (users) => {
        const sources = [
            'desktop',
            'webActivity',
            'webLogin',
            'download',
            'googleSSO',
            'microsoftSSO',
            'azure',
            'google'
        ];

        const result = {};

        sources.forEach((source) => {
            const found = users?.some((u) => u.sources[source]);
            result[source] = found ? 1 : 0;
        });

        return result;
    };

    /**
     * Retrieves a nested value from an object using a dot-notated path.
     *
     * @param obj - The object to retrieve the value from.
     * @param path - The dot-notated path to the desired value.
     * @returns The value at the specified path, or null if not found.
     */
    const getNestedValue = (obj, path) =>
        path
            .split('.')
            .reduce(
                (current, key) =>
                    current && current[key] !== undefined ? current[key] : null,
                obj
            );

    /**
     * Extracts the value of a specific query parameter from a URL
     * @param {string} params - The name of the query parameter to extract
     * @param {string} [url] - Optional URL to parse. If not provided, uses current window location
     * @returns {string|null} The value of the query parameter if found, null otherwise
     * @example
     * // Get 'id' parameter from current URL
     * getQueryParams('id')
     *
     * // Get 'name' parameter from specific URL
     * getQueryParams('name', 'https://example.com?name=test')
     */
    const getQueryParams = (params, url) => {
        const href = url || window.location.href;
        const reg = new RegExp(`[?&]${params}=([^&#]*)`, 'i');
        const queryString = reg.exec(href);
        return queryString ? queryString[1] : null;
    };

    /**
     * Parses query parameters from a URL string into an object
     * @param {string} qs - The URL string containing query parameters
     * @returns {Object} Object containing parsed query parameters as key-value pairs
     * @example
     * // Returns {name: 'test', id: '123'}
     * getQueryParamsFromString('http://example.com?name=test&id=123')
     *
     * // Returns {}
     * getQueryParamsFromString('http://example.com')
     */
    const getQueryParamsFromString = (qs) => {
        if (qs.indexOf('?') === -1) return {};

        const splitQs = qs.split('?')[1].split('+').join(' ');

        const params = {};
        let tokens;
        const re = /[?&]?([^=]+)=([^&]*)/g;

        // TODO: Fix lint issue
        // eslint-disable-next-line no-cond-assign
        while ((tokens = re.exec(splitQs))) {
            params[decodeURIComponent(tokens[1])] = decodeURIComponent(
                tokens[2]
            );
        }

        return params;
    };

    /**
     * Gets activity counts and SSO flags from a row object
     * @param {unknown} row - The row object containing activity data
     * @param {number} row.desktopActivityCount - Count of desktop activities
     * @param {number} row.cloudActivityCount - Count of web/cloud activities
     * @param {number} row.cloudLoginCount - Count of web/cloud logins
     * @param {number} row.downloadActivityCount - Count of download activities
     * @param {boolean} row.googleSSO - Google SSO enabled flag
     * @param {boolean} row.microsoftSSO - Microsoft SSO enabled flag
     * @param {number} row.azureSignInCount - Count of Azure sign-ins
     * @param {number} row.googleSignInCount - Count of Google sign-ins
     * @param {number} row.oktaSignInCount - Count of Okta sign-ins
     * @param {number} row.quickScanCount - Count of quick scans
     * @returns {Record<string, number | boolean>} Object containing activity counts and SSO flags
     * @example
     * const row = {
     *   desktopActivityCount: 5,
     *   cloudActivityCount: 2,
     *   cloudLoginCount: 1,
     *   downloadActivityCount: 3,
     *   googleSSO: true,
     *   microsoftSSO: false,
     *   azureSignInCount: 0,
     *   googleSignInCount: 2,
     *   oktaSignInCount: 1,
     *   quickScanCount: 1
     * };
     * getSources(row);
     * // Returns:
     * // {
     * //   desktop: 5,
     * //   webActivity: 2,
     * //   webLogin: 1,
     * //   download: 3,
     * //   googleSSO: true,
     * //   microsoftSSO: false,
     * //   azure: 0,
     * //   google: 2,
     * //   okta: 1,
     * //   quickScan: 1
     * // }
     */
    // We want this order, therefore disabling the sort rule
    const getSources = (row) => ({
        desktop: row.desktopActivityCount,
        webActivity: row.cloudActivityCount,
        webLogin: row.cloudLoginCount,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        download: row.downloadActivityCount,
        googleSSO: row.googleSSO,
        microsoftSSO: row.microsoftSSO,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        azure: row.azureSignInCount,
        google: row.googleSignInCount,
        okta: row.oktaSignInCount,
        quickScan: row.quickScanCount
    });

    // Will probably refactor the BE so that it returns the same keys as in getSources to avoid duplication
    // We want this order, therefore disabling the sort rule
    /**
     * Maps login source data from backend format to frontend format
     * @param {Object} sources - Object containing login sources in backend format
     * @param {boolean} sources.LOGIN - Web login activity flag
     * @param {boolean} sources.GOOGLE_SSO - Google SSO login activity flag
     * @param {boolean} sources.MICROSOFT_SSO - Microsoft SSO login activity flag
     * @param {boolean} sources.AZURE_SIGN_IN - Azure sign in activity flag
     * @param {boolean} sources.QUICK_SCAN_LOGIN - Quick scan login activity flag
     * @returns {Object} Object with mapped login source data
     * @example
     * const sources = {
     *   LOGIN: true,
     *   GOOGLE_SSO: false,
     *   MICROSOFT_SSO: true,
     *   AZURE_SIGN_IN: false,
     *   QUICK_SCAN_LOGIN: true
     * };
     * getSourcesLoginForAccounts(sources);
     * // Returns:
     * // {
     * //   webLogin: true,
     * //   googleSSO: false,
     * //   microsoftSSO: true,
     * //   azure: false,
     * //   quickScan: true
     * // }
     */
    const getSourcesLoginForAccounts = (sources) => ({
        webLogin: sources.LOGIN,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        googleSSO: sources.GOOGLE_SSO,
        microsoftSSO: sources.MICROSOFT_SSO,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        azure: sources.AZURE_SIGN_IN,
        okta: sources.OKTA_SIGN_IN,
        quickScan: sources.QUICK_SCAN_LOGIN
    });

    /**
     * Converts source flags to binary values (1 for true, 0 for false)
     * @param {Object} sources - Object containing source flags
     * @param {boolean} sources.desktop - Desktop source flag
     * @param {boolean} sources.webActivity - Web activity source flag
     * @param {boolean} sources.webLogin - Web login source flag
     * @param {boolean} sources.download - Download source flag
     * @param {boolean} sources.googleSSO - Google SSO source flag
     * @param {boolean} sources.microsoftSSO - Microsoft SSO source flag
     * @param {boolean} sources.azure - Azure source flag
     * @param {boolean} sources.google - Google source flag
     * @returns {Object} Object with source flags converted to 1 or 0
     * @example
     * const sources = {
     *   desktop: true,
     *   webActivity: false,
     *   webLogin: true,
     *   download: false,
     *   googleSSO: true,
     *   microsoftSSO: false,
     *   azure: true,
     *   google: false
     * };
     * getSourcesViaLink(sources);
     * // Returns:
     * // {
     * //   desktop: 1,
     * //   webActivity: 0,
     * //   webLogin: 1,
     * //   download: 0,
     * //   googleSSO: 1,
     * //   microsoftSSO: 0,
     * //   azure: 1,
     * //   google: 0
     * // }
     */
    // We want this order, therefore disabling the sort rule
    const getSourcesViaLink = (sources) => ({
        desktop: sources.desktop ? 1 : 0,
        webActivity: sources.webActivity ? 1 : 0,
        webLogin: sources.webLogin ? 1 : 0,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        download: sources.download ? 1 : 0,
        googleSSO: sources.googleSSO ? 1 : 0,
        microsoftSSO: sources.microsoftSSO ? 1 : 0,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        azure: sources.azure ? 1 : 0,
        google: sources.google ? 1 : 0
    });

    /**
     * Gets sources with activity flags from a row object
     * @param {Object} row - The row object containing activity flags
     * @param {boolean} row.desktopActivity - Desktop activity flag
     * @param {boolean} row.cloudActivity - Cloud/web activity flag
     * @param {boolean} row.cloudLogin - Cloud/web login flag
     * @param {boolean} row.downloadActivity - Download activity flag
     * @param {boolean} row.googleSSO - Google SSO flag
     * @param {boolean} row.microsoftSSO - Microsoft SSO flag
     * @param {boolean} row.azureSignInActivity - Azure sign in activity flag
     * @param {boolean} row.googleSignInActivity - Google sign in activity flag
     * @param {boolean} row.quickScanLogin - Quick scan login flag
     * @returns {Record<string, number | boolean>} Object containing activity flags for each source
     * @example
     * const row = {
     *   desktopActivity: true,
     *   cloudActivity: false,
     *   cloudLogin: true,
     *   downloadActivity: false,
     *   googleSSO: true,
     *   microsoftSSO: false,
     *   azureSignInActivity: true,
     *   googleSignInActivity: false,
     *   quickScanLogin: true
     * };
     * getSourcesWithActivity(row);
     * // Returns:
     * // {
     * //   desktop: true,
     * //   webActivity: false,
     * //   webLogin: true,
     * //   download: false,
     * //   googleSSO: true,
     * //   microsoftSSO: false,
     * //   azure: true,
     * //   google: false,
     * //   quickScan: true
     * // }
     */
    // We want this order, therefore disabling the sort rule
    const getSourcesWithActivity = (row) => ({
        desktop: row.desktopActivity,
        webActivity: row.cloudActivity,
        webLogin: row.cloudLogin,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        download: row.downloadActivity,
        googleSSO: row.googleSSO,
        microsoftSSO: row.microsoftSSO,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        azure: row.azureSignInActivity,
        google: row.googleSignInActivity,
        okta: row.oktaSignInActivity,
        quickScan: row.quickScanLogin
    });

    /**
     * Marketing links users to ASM to kick off an ANM trial and the URL includes several marketing attributes that we need to parse and send to an endpoint.
     * The params we want to send to marketing always begin with markAttr_. All other params can be ignored.
     * Usage:
     * const utmFields = getUTMFieldsFromURL(url);
     *
     * @param {*} string // E.g., window.location.href
     * @return {object} // E.g., {"markAttr_formID":"1524","markAttr_utm_campaign":"campaign","markAttr_utm_source":"CXN-newsletter","markAttr_pendo":"guideXYZ","markAttr_utm_medium":"email","markAttr_formID_s":"1524"}
     */
    const getUTMFieldsFromURL = (url) => {
        const newURL = new URL(url);
        const params = new URLSearchParams(decodeURIComponent(newURL.search));
        const utmFields = {};
        const utmPrefix = /^markAttr_.+/;

        params.forEach((v, k) => {
            if (utmPrefix.test(k)) {
                utmFields[k] = v;
            }
        });
        return utmFields;
    };

    /**
     * Checks if a value is a string representation of an array
     * @param {*} val - The value to check
     * @returns {boolean} True if value is a string starting with '[' and ending with ']', false otherwise
     * @example
     * isArrayString('[1,2,3]') // Returns true
     * isArrayString('hello') // Returns false
     * isArrayString(['a','b']) // Returns false
     */
    const isArrayString = (val) =>
        typeof val === 'string' && val.startsWith('[') && val.endsWith(']');

    /**
     * Checks if a given date is more than 30 days in the past.
     *
     * @param {string | Date | null | undefined} dateValue - The date to check (can be a Date object or an ISO string).
     * @returns {boolean} True if the date is valid and more than 30 days ago, false otherwise.
     */
    const isDateMoreThan30DaysAgo = (dateValue) => {
        // Handle null, undefined, or empty string inputs
        if (!dateValue) {
            return false;
        }

        let inputDate;
        // Attempt to parse if it's a string, otherwise assume it might be a Date object
        if (typeof dateValue === 'string') {
            inputDate = parseISO(dateValue); // Use parseISO for robust ISO 8601 parsing
        } else if (dateValue instanceof Date) {
            inputDate = dateValue;
        } else {
            // If it's neither a string nor a Date object, consider it invalid
            return false;
        }

        // Check if the input resulted in a valid Date object
        if (!isValid(inputDate)) {
            return false;
        }

        // Calculate the date 30 days ago from the current time
        const thirtyDaysAgo = subDays(new Date(), 30);

        // Compare the dates based on the start of the day to ignore time components
        const inputStartOfDay = startOfDay(inputDate);
        const thirtyDaysAgoStartOfDay = startOfDay(thirtyDaysAgo);

        // Return true if the input date is strictly earlier than 30 days ago
        return inputStartOfDay.getTime() < thirtyDaysAgoStartOfDay.getTime();
    };

    /**
     * Validates if a given text is a valid email address
     * @param {string} text - The text to validate as an email address
     * @returns {boolean} True if text is a valid email address, false otherwise
     * @example
     * isEmail('test@example.com') // Returns true
     * isEmail('invalid.email') // Returns false
     * isEmail('user@domain.co.uk') // Returns true
     * isEmail('') // Returns false
     */
    const isEmail = (text) =>
        String(text)
            .toLowerCase()
            .match(
                // eslint-disable-next-line max-len
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );

    /**
     * Checks if a value is empty (null, undefined, or an empty object)
     * @param {*} obj - The value to check for emptiness
     * @returns {boolean} True if the value is empty, false otherwise
     * @example
     * isEmpty(null) // Returns true
     * isEmpty(undefined) // Returns true
     * isEmpty({}) // Returns true
     * isEmpty({a: 1}) // Returns false
     * isEmpty([]) // Returns true
     * isEmpty([1,2,3]) // Returns false
     */
    const isEmpty = (obj) => {
        if (!obj) {
            return true;
        }

        if (Object.keys(obj).length === 0) {
            return true;
        }

        return false;
    };

    /**
     * Checks if a deployment has Google authentication enabled
     * @param {Object | null} providerConfig - The provider configuration object
     * @param {string} providerConfig.providerType - The type of provider
     * @param {boolean} providerConfig.enabled - Whether the provider is enabled
     * @returns {boolean} True if Google authentication is enabled for this deployment, false otherwise
     * @example
     * const config = {
     *   providerType: 'Google',
     *   enabled: true
     * };
     * isGoogleEnabledDeployment(config); // Returns true
     */
    const isGoogleEnabledDeployment = (providerConfig) =>
        providerConfig?.providerType === ProviderType.Google &&
        providerConfig?.enabled;

    // Not used
    /**
     * Checks if a value is a JSON object
     * @param {*} str - The value to check
     * @returns {boolean} True if the value is an object, false otherwise
     * @example
     * isJSON({a: 1}) // Returns true
     * isJSON('{"a": 1}') // Returns false
     * isJSON(null) // Returns false
     * isJSON([1,2,3]) // Returns true
     */
    const isJSON = (str) => {
        try {
            return typeof str === 'object';
        } catch (e) {
            return false;
        }
    };

    /**
     * Checks if a deployment has Microsoft Entra ID (formerly Azure AD) authentication enabled
     * @param {Object | null} providerConfig - The provider configuration object
     * @param {string} providerConfig.providerType - The type of provider
     * @param {boolean} providerConfig.enabled - Whether the provider is enabled
     * @param {string} providerConfig.applicationId - The application ID for Microsoft Entra ID
     * @param {boolean} providerConfig.useAsmAppCredentials - Whether to use ASM app credentials
     * @returns {boolean} True if Microsoft Entra ID authentication is enabled for this deployment, false otherwise
     * @example
     * const config = {
     *   providerType: 'Microsoft',
     *   enabled: true,
     *   applicationId: 'abc123',
     *   useAsmAppCredentials: false
     * };
     * isMicrosoftEntraIDEnabledDeployment(config); // Returns true
     */
    const isMicrosoftEntraIDEnabledDeployment = (providerConfig) =>
        providerConfig?.providerType === ProviderType.Microsoft &&
        providerConfig?.enabled &&
        (providerConfig?.applicationId !== Defaults.id0 ||
            providerConfig?.useAsmAppCredentials);

    /**
     * Checks if a value can be parsed as a valid number
     * @param {*} value - The value to check
     * @returns {boolean} True if value can be parsed as a number, false otherwise
     * @example
     * isNum('123') // Returns true
     * isNum('12.34') // Returns true
     * isNum('abc') // Returns false
     * isNum(null) // Returns false
     * isNum(undefined) // Returns false
     */
    const isNum = (value) => !Number.isNaN(parseFloat(value));

    /**
     * Checks if a value is a non-null object
     * @param {*} variable - The value to check
     * @returns {boolean} True if value is an object and not null, false otherwise
     * @example
     * isObject({}) // Returns true
     * isObject({a: 1}) // Returns true
     * isObject(null) // Returns false
     * isObject('string') // Returns false
     * isObject([1,2,3]) // Returns true since arrays are objects
     */
    const isObject = (variable) => {
        if (typeof variable === 'object' && variable !== null) {
            return true;
        }
        return false;
    };

    /**
     * Checks if a nested property exists in an object
     * @param {Object} obj - The object to check
     * @param {...string | ...number} args - Property names to check, in order of nesting
     * @returns {boolean} True if the full property path exists, false otherwise
     * @example
     * const obj = {
     *   user: {
     *     profile: {
     *       name: 'John'
     *     }
     *   }
     * };
     * isset(obj, 'user', 'profile', 'name') // Returns true
     * isset(obj, 'user', 'settings') // Returns false
     * isset(null, 'prop') // Returns false
     * isset({}, 'prop') // Returns false
     */
    const isset = (obj /* , level1, level2, ... levelN */, ...args) => {
        if (!obj) {
            return false;
        }

        for (let i = 0; i < args.length; i += 1) {
            if (!obj) {
                return false;
            }

            // eslint-disable-next-line no-prototype-builtins
            if (!obj.hasOwnProperty(args[i])) {
                return false;
            }
            // eslint-disable-next-line no-param-reassign
            obj = obj[args[i]];
        }
        return true;
    };

    /**
     * Checks if a user's trial has expired based on session settings
     * @param {Object} session - The session object containing trial status
     * @param {Object} session.sessionSettings - Session settings object
     * @param {boolean} session.sessionSettings.expiredTrial - Flag indicating if trial is expired
     * @returns {boolean} True if trial is expired or session settings are invalid, false otherwise
     * @example
     * const session = {
     *   sessionSettings: {
     *     expiredTrial: false
     *   }
     * };
     * isTrialExpired(session); // Returns false
     *
     * isTrialExpired({}); // Returns true
     * isTrialExpired(null); // Returns true
     */
    const isTrialExpired = (session) => {
        if (!isset(session, 'sessionSettings', 'expiredTrial')) {
            return true;
        }
        return session.sessionSettings.expiredTrial;
    };

    /**
     * Validates if a string is a valid UUID
     * @param {string} id - The string to validate as a UUID
     * @returns {boolean} True if string is a valid UUID, false otherwise
     * @example
     * isUUID('123e4567-e89b-12d3-a456-426614174000') // Returns true
     * isUUID('not-a-uuid') // Returns false
     * isUUID(null) // Returns false
     */
    const isUUID = (id) => uuidValidate(id);

    /**
     * Joins array elements with a separator
     * @param {Array} arr - The array to join
     * @param {string} separator - The separator to use between elements
     * @returns {string|*} Joined string if input is array, otherwise returns input unchanged
     * @example
     * join(['a','b','c'], '-') // Returns "a-b-c"
     * join(['hello', 'world'], ' ') // Returns "hello world"
     * join('not an array', '-') // Returns "not an array"
     * join(null, ',') // Returns null
     */
    const join = (arr, separator) => {
        if (!Array.isArray(arr)) {
            return arr;
        }
        return arr.join(separator);
    };

    /**
     * Pads an array to a specified length with a given value
     * @param {Array} array - The array to pad
     * @param {number} length - The desired length of the resulting array
     * @param {*} padValue - The value to use for padding
     * @param {string} [leftOrRight='left'] - Direction to pad ('left' or 'right')
     * @returns {Array} New array padded to specified length
     * @example
     * padArray([1,2], 4, 0, 'left') // Returns [0,0,1,2]
     * padArray([1,2], 4, 0, 'right') // Returns [1,2,0,0]
     * padArray(null, 3, 0) // Returns []
     * padArray([1,2], 1, 0) // Returns [1,2] (no padding when length <= array.length)
     */
    const padArray = (array, length, padValue, leftOrRight = 'left') => {
        if (!array) {
            return [];
        }
        let padLength = length - array.length;
        if (padLength < 0 || Number.isNaN(padLength)) {
            padLength = 0;
        }
        return [
            ...(leftOrRight === 'left' ? [] : array),
            ...Array(padLength).fill(padValue),
            ...(leftOrRight === 'right' ? [] : array)
        ];
    };

    /**
     * Parses the current URL's query string into an object
     * @returns {Object} Object containing query parameters as key-value pairs
     * @example
     * // For URL "http://example.com?name=test&id=123"
     * parseQueryString() // Returns { name: "test", id: "123" }
     *
     * // For URL with no query params
     * parseQueryString() // Returns {}
     *
     * // For URL with empty param value
     * // "http://example.com?param="
     * parseQueryString() // Returns { param: undefined }
     */
    const parseQueryString = () => {
        const str = window.location.search;
        const objURL = {};

        const regex = /([^?=&]+)(=([^&]*))?/g;
        str.replace(regex, ($0, $1, $2, $3) => {
            objURL[$1] = $3;
        });

        return objURL;
    };

    /**
     * Sorts an array of objects by a specified key
     * @param {Array|*} v - Array of objects to sort, or any other value
     * @param {string} key - Object key to sort by
     * @returns {Array|*} Sorted array if input is array, otherwise returns input unchanged
     * @example
     * const arr = [{id: 2}, {id: 1}, {id: 3}];
     * sort(arr, 'id') // Returns [{id: 1}, {id: 2}, {id: 3}]
     *
     * sort('not an array', 'key') // Returns 'not an array'
     * sort(null, 'key') // Returns null
     */
    const sort = (v, key) => {
        if (Array.isArray(v)) {
            return v.sort((a, b) => {
                const valA = a[key];
                const valB = b[key];

                if (valA > valB) return 1;
                if (valA < valB) return -1;
                return 0;
            });
        }

        // handle objects later
        return v;
    };

    // TODO: Fix lint issues
    /**
     * Converts a string into a unique color by hashing the string and converting to HSL color space
     * @param {string} str - Input string to convert to color
     * @param {number} S - Saturation value (0-100)
     * @param {number} L - Lightness value (0-100)
     * @returns {string} Hex color code (e.g. "#ff0000")
     * @example
     * stringToColor("Hello", 50, 50) // Returns a hex color like "#7ab2c4"
     * stringToColor("Test", 100, 50) // Returns a fully saturated color
     * stringToColor("Example", 0, 50) // Returns a grayscale color
     */
    const stringToColor = (str, S, L) => {
        let s = S;
        let l = L;

        let hash = 0;
        for (let i = 0; i < str.length; i += 1) {
            // eslint-disable-next-line no-bitwise
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        let h = hash % 360;
        h /= 360;
        s /= 100;
        l /= 100;
        let r;
        let g;
        let b;
        if (s === 0) {
            // eslint-disable-next-line no-multi-assign
            r = g = b = l; // achromatic
        } else {
            const hue2rgb = (p, q, t) => {
                // eslint-disable-next-line no-param-reassign
                if (t < 0) t += 1;
                // eslint-disable-next-line no-param-reassign
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            };
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        const toHex = (x) => {
            const hex = Math.round(x * 255).toString(16);
            return hex.length === 1 ? `0${hex}` : hex;
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    /**
     * Converts a value to an array
     * @param {*} val - Value to convert to array
     * @returns {Array} Resulting array
     * @example
     * toArray(null) // Returns []
     * toArray([1,2,3]) // Returns [1,2,3]
     * toArray({a:1, b:2}) // Returns [1,2]
     * toArray('[1,2,3]') // Returns ['1','2','3']
     * toArray('{"a":1,"b":2}') // Returns [1,2]
     */
    const toArray = (val) => {
        if (!val) {
            return [];
        }

        if (Array.isArray(val)) {
            return val;
        }

        if (isObject(val)) {
            return Object.values(val);
        }

        if (isArrayString(val)) {
            return val.substr(1, val.length - 2).split(',');
        }

        try {
            const arr = JSON.parse(val);
            return Object.values(arr);
        } catch (e) {
            return [];
        }
    };

    /**
     * Converts a value to a sorted array string representation
     * @param {*} val - Value to convert
     * @returns {string} String representation of sorted array
     * @example
     * toSortedArrayString([3,1,2]) // Returns "[1,2,3]"
     * toSortedArrayString({b:2,a:1}) // Returns "[1,2]"
     * toSortedArrayString(null) // Returns "[]"
     */
    const toSortedArrayString = (val) => {
        const arr = toArray(val);
        return `[${arr.sort().join(',')}]`;
    };

    /**
     * Gets trial end date from tenant settings
     * @param {Object} tenantSettings - Tenant settings object
     * @param {string|Date} tenantSettings.trialEndDate - Trial end date
     * @returns {Date|false} Trial end date as Date object if valid, false otherwise
     * @example
     * trialEndDateFromTenantSettings({trialEndDate: '2024-01-01'}) // Returns Date object
     * trialEndDateFromTenantSettings({}) // Returns false
     * trialEndDateFromTenantSettings(null) // Returns false
     */
    const trialEndDateFromTenantSettings = (tenantSettings) =>
        !!tenantSettings?.trialEndDate &&
        new Date(tenantSettings?.trialEndDate);

    /**
     * Calculates days remaining in trial from tenant settings
     * @param {Object} tenantSettings - Tenant settings object
     * @param {string|Date|undefined} [tenantSettings.trialEndDate] - Trial end date
     * @returns {number} Number of days remaining in trial (0 if expired or invalid)
     * @example
     * // If today is 2024-01-01:
     * trialDaysLeft({trialEndDate: '2024-01-05'}) // Returns 4
     * trialDaysLeft({trialEndDate: '2023-12-31'}) // Returns 0
     * trialDaysLeft({}) // Returns 0
     */
    const trialDaysLeft = (tenantSettings) => {
        const trialEndDate = trialEndDateFromTenantSettings(tenantSettings);

        const daysLeft = trialEndDate
            ? differenceInDays(trialEndDate, new Date()) + 1
            : 0;

        return daysLeft;
    };

    /**
     * Capitalizes first character of a string
     * @param {string} string - Input string
     * @returns {string} String with first character capitalized
     * @example
     * ucfirst('hello') // Returns "Hello"
     * ucfirst('WORLD') // Returns "WORLD"
     * ucfirst('') // Returns ""
     */
    const ucfirst = (string) =>
        string.charAt(0).toUpperCase() + string.slice(1);

    /**
     * Converts text to uppercase and trims whitespace
     * @param {string} text - Input text
     * @returns {string} Uppercase trimmed string
     * @example
     * upperCase('hello ') // Returns "HELLO"
     * upperCase(' World') // Returns "WORLD"
     * upperCase('test') // Returns "TEST"
     */
    const upperCase = (text) => String(text).toUpperCase().trim();

    /**
     * Converts object of URL parameters to query string
     * @param {Object} params - Object containing URL parameters
     * @returns {string} URL-encoded query string
     * @example
     * urlParams({name: 'test', id: 123}) // Returns "name=test&id=123"
     * urlParams({q: 'search term'}) // Returns "q=search+term"
     * urlParams({}) // Returns ""
     */
    const urlParams = (params) => new URLSearchParams(params).toString();

    return {
        areEqual,
        arraysEqual,
        byteSizeFormatter,
        clearToasts,
        convertToBoolean,
        count,
        doesPieChartHaveData,
        getConsolidatesSourcesFromUsers,
        getNestedValue,
        getQueryParams,
        getQueryParamsFromString,
        getSources,
        getSourcesLoginForAccounts,
        getSourcesViaLink,
        getSourcesWithActivity,
        getUTMFieldsFromURL,
        isArrayString,
        isDateMoreThan30DaysAgo,
        isEmail,
        isEmpty,
        isGoogleEnabledDeployment,
        isJSON,
        isMicrosoftEntraIDEnabledDeployment,
        isNum,
        isObject,
        isset,
        isTrialExpired,
        isUUID,
        join,
        padArray,
        parseQueryString,
        sort,
        stringToColor,
        toArray,
        toSortedArrayString,
        trialDaysLeft,
        trialEndDateFromTenantSettings,
        ucfirst,
        upperCase,
        urlParams
    };
})();

export default Helpers;
